// Cockpit da aba "TODOS" — todos os indicadores vêm de dados reais já calculados/agregados no
// próprio módulo de Auditoria (nenhum valor mockado). Alguns dos indicadores do mock original não
// tinham correspondência real no sistema (nenhum workflow de revisão humana de achados, nenhuma
// tela com múltiplas empresas auditadas simultaneamente, nenhum controle de documentos/anexos
// pendentes) — foram substituídos por métricas reais equivalentes, documentadas abaixo:
//   - "Empresas em risco"      → Empresas na Auditoria (empresas exibidas / cadastradas no cliente)
//   - "Achados revisados"      → Módulos Conciliados (módulos sem nenhum impacto fiscal/financeiro)
//   - "Pendências do revisor"  → módulos (dos ~59) com pelo menos 1 AVISO (aviso = "requer
//     validação manual" em todos os módulos desta sessão, o mais próximo que existe hoje de uma
//     "pendência de revisão")
//   - "Documentos pendentes"   → Módulos Pendentes de Carregamento (falharam no Promise.allSettled)
//   - "Dependências externas"  → módulos que dependem de serviço externo (BCB Selic/PTAX): Cambial,
//     Round Tripping, Mutação Volátil — contagem estática e real, não uma métrica de falha (não
//     existe rastreamento de falha de dependência externa por chamada hoje).

export interface GaugeValor {
  valor: number;
  max: number;
}

export type RiscoGeral = 'Alto' | 'Médio' | 'Baixo' | 'Controlado';

export interface EntradaCockpit {
  totalErros: number;
  totalAlertas: number;
  totalAvisos: number;
  impactoFiscalTotal: number;
  impactoFinanceiroTotal: number;
  faturamentoPeriodo: number;
  totalTributos: number;
  tributosComDivergencia: number;
  totalTrimestresAnalisados: number;
  compensacoesPendentes: number;
  contasComNaturezaInvertida: number;
  contasConciliadas: number;
  empresasSelecionadas: number;
  totalEmpresasCadastradas: number;
  totalModulosPossiveis: number;
  modulosComImpacto: number;
  modulosComErro: number;
  modulosComAviso: number;
  modulosComFalhaCarregamento: number;
  totalModulosTentados: number;
}

export interface IndicadoresCockpit {
  progressoAuditoria: GaugeValor;
  errosCriticos: GaugeValor;
  alertas: GaugeValor;
  avisos: GaugeValor;
  impactoFiscalTotal: GaugeValor;
  impactoFinanceiroTotal: GaugeValor;
  tributosComDivergencia: GaugeValor;
  compensacoesPendentes: GaugeValor;
  saldosInvertidos: GaugeValor;
  empresasNaAuditoria: GaugeValor;
  modulosConciliados: GaugeValor;
  pendenciasDoRevisor: GaugeValor;
  modulosComImpacto: GaugeValor;
  areasCriticas: GaugeValor;
  dependenciasExternas: GaugeValor;
  modulosPendentesCarregamento: GaugeValor;
  saudeGeral: {
    planejadoPct: number;
    realizadoPct: number;
    riscoGeral: RiscoGeral;
    achadosCriticos: number;
  };
}

// Módulos com dependência de serviço externo real (BCB Selic/PTAX): Cambial, Round Tripping,
// Mutação Volátil. Contagem estática — não há hoje rastreamento de falha por chamada externa.
export const DEPENDENCIAS_EXTERNAS_CONHECIDAS = 3;

// COR-190: o "de X" mostrado sempre é o teto REAL (ex.: faturamento do período), mesmo quando o
// valor o ultrapassa (ex.: impacto fiscal somado de ~59 módulos independentes pode legitimamente
// superar o faturamento de um único ano — cada módulo tem sua própria base de cálculo, não
// necessariamente proporcional à receita). Antes, o "de X" era inflado para sempre bater com o
// valor (Math.max(max, valor)), o que escondia o quanto o valor realmente excedia o teto real —
// "R$ 467,8 mil de R$ 467,8 mil" parecia "exatamente no limite", quando na verdade era R$ 467,8
// mil sobre um faturamento de R$ 442,3 mil (106%). O estouro visual (>100%) é tratado só na
// contagem do arco/ponteiro do gauge (AuditoriaGauge.tsx já limita a 100%), nunca no texto.
function gauge(valor: number, max: number): GaugeValor {
  return { valor, max };
}

export function calcularIndicadoresCockpit(entrada: EntradaCockpit): IndicadoresCockpit {
  const totalAchados = entrada.totalErros + entrada.totalAlertas + entrada.totalAvisos;
  const modulosConciliados = Math.max(0, entrada.totalModulosPossiveis - entrada.modulosComImpacto);
  const modulosCarregadosComSucesso = Math.max(0, entrada.totalModulosTentados - entrada.modulosComFalhaCarregamento);
  const realizadoPct = entrada.totalModulosTentados > 0
    ? Math.round((modulosCarregadosComSucesso / entrada.totalModulosTentados) * 100)
    : 100;

  const riscoGeral: RiscoGeral =
    entrada.totalErros > 0 ? 'Alto' :
    entrada.totalAlertas > 0 ? 'Médio' :
    entrada.totalAvisos > 0 ? 'Baixo' :
    'Controlado';

  return {
    progressoAuditoria: gauge(realizadoPct, 100),
    errosCriticos: gauge(entrada.totalErros, totalAchados),
    alertas: gauge(entrada.totalAlertas, totalAchados),
    avisos: gauge(entrada.totalAvisos, totalAchados),
    impactoFiscalTotal: gauge(entrada.impactoFiscalTotal, entrada.faturamentoPeriodo),
    impactoFinanceiroTotal: gauge(entrada.impactoFinanceiroTotal, entrada.faturamentoPeriodo),
    tributosComDivergencia: gauge(entrada.tributosComDivergencia, entrada.totalTributos),
    compensacoesPendentes: gauge(entrada.compensacoesPendentes, entrada.totalTrimestresAnalisados),
    saldosInvertidos: gauge(entrada.contasComNaturezaInvertida, entrada.contasComNaturezaInvertida + entrada.contasConciliadas),
    empresasNaAuditoria: gauge(entrada.empresasSelecionadas, entrada.totalEmpresasCadastradas),
    modulosConciliados: gauge(modulosConciliados, entrada.totalModulosPossiveis),
    pendenciasDoRevisor: gauge(entrada.modulosComAviso, entrada.totalModulosPossiveis),
    modulosComImpacto: gauge(entrada.modulosComImpacto, entrada.totalModulosPossiveis),
    areasCriticas: gauge(entrada.modulosComErro, entrada.totalModulosPossiveis),
    dependenciasExternas: gauge(DEPENDENCIAS_EXTERNAS_CONHECIDAS, entrada.totalModulosPossiveis),
    modulosPendentesCarregamento: gauge(entrada.modulosComFalhaCarregamento, entrada.totalModulosTentados),
    saudeGeral: {
      planejadoPct: 100,
      realizadoPct,
      riscoGeral,
      achadosCriticos: entrada.totalErros,
    },
  };
}
